import React, { useState, useEffect, useCallback } from 'react';
import { useSeason } from '../../context/SeasonContext';
import api from '../../api';
import styles from '../../styles/AdminSeasonOverride.module.css';

const healthClassMap = {
  aligned: styles.healthAligned,
  rollover_pending: styles.healthPending,
  misaligned: styles.healthMisaligned
};

const statusLabelMap = {
  current: 'Current season',
  archived: 'Archived',
  missing_archive: 'Missing archive',
  no_data: 'No data'
};

const AdminSeasonOverride = () => {
  const {
    currentSeason,
    availableSeasons,
    adminOverride,
    setAdminSeasonOverride,
    clearAdminSeasonOverride,
    refreshCurrentSeason
  } = useSeason();

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [rolloverStatus, setRolloverStatus] = useState(null);
  const [targetSeason, setTargetSeason] = useState(currentSeason);

  const loadRolloverStatus = useCallback(async () => {
    try {
      const response = await api.getSeasonRolloverStatus();
      setRolloverStatus(response.data);
    } catch (error) {
      console.error('Error loading rollover status:', error);
    }
  }, []);

  useEffect(() => {
    loadRolloverStatus();
  }, [loadRolloverStatus]);

  useEffect(() => {
    if (currentSeason) {
      setTargetSeason(currentSeason);
    }
  }, [currentSeason]);

  const handleSeasonChange = async (event) => {
    const selectedSeason = parseInt(event.target.value, 10);
    setLoading(true);
    setMessage('');

    try {
      if (selectedSeason === currentSeason) {
        await clearAdminSeasonOverride();
        setMessage('Admin override cleared');
      } else {
        await setAdminSeasonOverride(selectedSeason);
        setMessage(`Admin override set to ${selectedSeason}`);
      }
    } catch (error) {
      setMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRollover = async () => {
    if (!window.confirm(
      'Run full season rollover?\n\n' +
      'This will archive the active season stats, reset live user stats, and advance active_season to the next calendar year.\n\n' +
      'This cannot be undone.'
    )) {
      return;
    }

    setLoading(true);
    setMessage('Running season rollover...');

    try {
      const response = await api.triggerAdminRollover();
      await refreshCurrentSeason();
      await loadRolloverStatus();
      setMessage(`Season rollover successful.\n\nNow on season ${response.data.newSeason}.`);
    } catch (error) {
      setMessage(`Error: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRearchive = async (season) => {
    if (!window.confirm(`Re-archive season ${season} from pick data? This replaces any existing archived stats for that season.`)) {
      return;
    }

    setLoading(true);
    setMessage(`Re-archiving season ${season}...`);

    try {
      await api.rearchiveSeason(season);
      await loadRolloverStatus();
      setMessage(`Season ${season} re-archived successfully from pick data.`);
    } catch (error) {
      setMessage(`Error: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleMigrationOnly = async () => {
    if (!window.confirm(`Migrate user statistics for season ${currentSeason} to the historical table without advancing the active season?`)) {
      return;
    }

    setLoading(true);
    setMessage('Starting data migration...');

    try {
      await api.migrateUserStats(currentSeason);
      await loadRolloverStatus();
      setMessage(`Migrated ${currentSeason} user statistics to the historical table. Live stats unchanged.`);
    } catch (error) {
      setMessage(`Error: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomSeasonMigration = async () => {
    if (!targetSeason || targetSeason === currentSeason) {
      setMessage('Please select a different target season for migration');
      return;
    }

    if (!window.confirm(`Migrate current user statistics into historical season ${targetSeason}?`)) {
      return;
    }

    setLoading(true);
    setMessage(`Starting migration to season ${targetSeason}...`);

    try {
      await api.migrateUserStats(targetSeason);
      await loadRolloverStatus();
      setMessage(`Migrated current user statistics to historical season ${targetSeason}.`);
    } catch (error) {
      setMessage(`Error: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.adminSeasonOverride}>
      <h3>Season Management</h3>

      <div className={styles.section}>
        <h4>Rollover Status</h4>
        {rolloverStatus ? (
          <>
            <div className={`${styles.healthBanner} ${healthClassMap[rolloverStatus.health] || ''}`}>
              <strong>{rolloverStatus.health.replace('_', ' ').toUpperCase()}</strong>
              <span>{rolloverStatus.healthMessage}</span>
            </div>

            <div className={styles.statusGrid}>
              <div><span>Active season</span><strong>{rolloverStatus.activeSeason}</strong></div>
              <div><span>Calendar loaded</span><strong>{rolloverStatus.calendarSeasons.join(', ')}</strong></div>
              <div><span>Next season calendar</span><strong>{rolloverStatus.rolloverCriteria.nextCalendarAvailable ? rolloverStatus.rolloverCriteria.targetSeason : 'Not available'}</strong></div>
              <div><span>March 1 passed</span><strong>{rolloverStatus.rolloverCriteria.march1Passed ? 'Yes' : 'No'}</strong></div>
              <div><span>Auto rollover ready</span><strong>{rolloverStatus.rolloverCriteria.met ? 'Yes' : 'No'}</strong></div>
            </div>

            <table className={styles.statusTable}>
              <thead>
                <tr>
                  <th>Season</th>
                  <th>Status</th>
                  <th>Users</th>
                  <th>Last updated</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rolloverStatus.seasonArchiveStatus.map(row => (
                  <tr key={row.season}>
                    <td>{row.season}</td>
                    <td>{statusLabelMap[row.status] || row.status}</td>
                    <td>{row.userCount ?? '—'}</td>
                    <td>{row.lastUpdated ? new Date(row.lastUpdated).toLocaleString() : '—'}</td>
                    <td>
                      {row.status === 'missing_archive' && (
                        <button
                          type="button"
                          className={styles.repairButton}
                          disabled={loading}
                          onClick={() => handleRearchive(row.season)}
                        >
                          Re-archive
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : (
          <p>Loading rollover status...</p>
        )}
      </div>

      <div className={styles.section}>
        <h4>Current Season</h4>
        <p>System active season: <strong>{currentSeason}</strong></p>
      </div>

      <div className={styles.section}>
        <h4>Admin Override</h4>
        <p>Override your session to view the site from a different season perspective:</p>

        <select
          value={adminOverride || currentSeason}
          onChange={handleSeasonChange}
          disabled={loading}
          className={styles.seasonSelect}
        >
          {availableSeasons.map(season => (
            <option key={season} value={season}>
              {season} {season === currentSeason ? '(Current)' : ''}
            </option>
          ))}
        </select>

        {adminOverride && (
          <div className={styles.overrideInfo}>
            <p>⚠️ You are viewing the site in <strong>{adminOverride} Archive</strong> mode</p>
            <p>This only affects your session and will reset on logout</p>
          </div>
        )}
      </div>

      <div className={styles.section}>
        <h4>Season Rollover Actions</h4>
        <p>Unified rollover archives stats, resets live user stats, and advances the active season together.</p>

        <div className={styles.buttonGroup}>
          <button
            onClick={handleRollover}
            disabled={loading}
            className={styles.rolloverButton}
          >
            {loading ? 'Processing...' : 'Run Full Season Rollover'}
          </button>

          <button
            onClick={handleMigrationOnly}
            disabled={loading}
            className={styles.migrationButton}
          >
            {loading ? 'Processing...' : `Migrate ${currentSeason} Data Only`}
          </button>
        </div>

        <div className={styles.helpText}>
          <p><strong>Run Full Season Rollover:</strong> Archives active season, resets live stats, and advances active season (same path as automatic rollover).</p>
          <p><strong>Migrate Data Only:</strong> Repair/archive tool — does not advance the active season.</p>
        </div>

        <div className={styles.customMigrationSection}>
          <h5>Custom Season Migration</h5>
          <p>Migrate current live user stats into a specific historical season label:</p>

          <div className={styles.targetSeasonSelector}>
            <label htmlFor="target-season">Target Season:</label>
            <select
              id="target-season"
              value={targetSeason}
              onChange={(e) => setTargetSeason(parseInt(e.target.value, 10))}
              disabled={loading}
              className={styles.seasonSelect}
            >
              {availableSeasons.map(season => (
                <option key={season} value={season}>
                  {season}
                </option>
              ))}
            </select>
          </div>

          <button
            onClick={handleCustomSeasonMigration}
            disabled={loading || targetSeason === currentSeason}
            className={styles.customMigrationButton}
          >
            {loading ? 'Processing...' : `Migrate to Season ${targetSeason}`}
          </button>
        </div>
      </div>

      {message && (
        <div className={`${styles.message} ${message.includes('Error') ? styles.error : styles.success}`}>
          {message}
        </div>
      )}
    </div>
  );
};

export default AdminSeasonOverride;
